import type { ReactFlowState } from "reactflow";
import { CANVAS_LAYERS } from "src/pages/projects/library/canvas/canvas-layers";

/** Clears the selected card's 2px ring and its 2px offset while still reading as that card's bar. */
export const BAR_GAP = 8;
/** An md IconButton plus the bar's 4px padding on each side. */
const BAR_HEIGHT = 40;
export const BAR_LAYER_STYLE = { zIndex: CANVAS_LAYERS.itemBar };
/** The canvas title row floats over the top 56px (16px inset plus the 40px header pill); the bar flips below the item rather than slide under it. */
export const HEADER_BAND = 56;

/** Marks the canvas beside the agent panel, the area a popup on a card stays inside. */
export const CANVAS_AREA = "data-canvas-area";

/** Where a popup anchored at `element` must stay: the canvas when it sits on one, so it never spills onto the agent panel, else its clipping parents. */
export function canvasBoundary(element: HTMLElement | null | undefined) {
  return element?.closest<HTMLElement>(`[${CANVAS_AREA}]`) ?? "clippingParents";
}

/** Above the item, else below it, else pinned under the title row when neither edge has room on screen. */
export function placeBar(top: number, bottom: number, canvasHeight: number) {
  if (top - BAR_GAP - BAR_HEIGHT >= HEADER_BAND)
    return { isBelow: false, offset: BAR_GAP };
  if (
    bottom + BAR_GAP >= HEADER_BAND &&
    bottom + BAR_GAP + BAR_HEIGHT <= canvasHeight
  )
    return { isBelow: true, offset: BAR_GAP };
  return { isBelow: false, offset: top - HEADER_BAND - BAR_GAP - BAR_HEIGHT };
}

/** How far to slide the bar sideways so it stays on the canvas when the item's center is off-screen. */
export function shiftBar(
  center: number,
  barWidth: number,
  canvasWidth: number,
) {
  const inset = barWidth / 2 + BAR_GAP;
  return Math.min(Math.max(center, inset), canvasWidth - inset) - center;
}

/** A card is dragging, so a bar over the selection steps aside. The canvas hides its bars itself while a selection box is drawn. */
export const selectIsMoving = (state: ReactFlowState) => {
  for (const node of state.nodeInternals.values())
    if (node.dragging) return true;
  return false;
};
